// DeployLifecycleTimeline — fases ao vivo do deploy baseadas no ciclo
// de vida observavel pelo frontend (lifecycle phases). Por que 3:
//
//   1. Enviando comando        — POST /deploy ate o servidor retornar commandId
//   2. Aguardando agente SSM   — SSM em Pending (commandId existe mas nao iniciou)
//   3. Executando deploy.sh    — SSM em InProgress ate Success/Failed
//
// Fases mais granulares (Pulling/Building/Recreating) NAO sao mostradas
// porque SSM nao stream-a output durante a execucao — so apareceriam
// como fake progress. Para o detalhe pos-execucao, ver DeployTimeline
// (retroativo, baseado em parseDeployOutput).

import { Group, Text, ThemeIcon, Timeline } from '@mantine/core'
import {
  IconCircleCheck,
  IconCircleX,
  IconClock,
  IconDeviceDesktopAnalytics,
  IconLoader2,
  IconPlayerPlay,
  IconSend,
} from '@tabler/icons-react'
import { motion } from 'framer-motion'
import { COLOR } from './tokens'
import { formatElapsed, useTicker } from './useTicker'

type LifecycleStatus = 'pending' | 'running' | 'success' | 'failed'

interface Phase {
  name: string
  icon: React.ComponentType<any>
  status: LifecycleStatus
  /** ms — when known (only for completed/active phases) */
  durationMs?: number
}

interface Props {
  /** True after POST /deploy returned the command_id */
  commandIdReceived: boolean
  /** Raw SSM status: '', 'Pending', 'InProgress', 'Success', 'Failed', etc */
  deployStatus: string
  /** Frontend top-level state */
  status: 'idle' | 'running' | 'done' | 'error'
  /** Epoch ms when the user clicked "Iniciar Deploy" */
  startedAt: number | null
  /** Epoch ms when we got the commandId back from POST /deploy */
  commandIdReceivedAt: number | null
  /** Epoch ms when we first saw deployStatus === 'InProgress' */
  inProgressAt: number | null
  /** Epoch ms when the operation reached a terminal state */
  endedAt: number | null
}

export function DeployLifecycleTimeline({
  commandIdReceived,
  deployStatus,
  status,
  startedAt,
  commandIdReceivedAt,
  inProgressAt,
  endedAt,
}: Props) {
  // Re-render every 1s for smooth live duration tick on the active phase
  useTicker(status === 'running' ? 1000 : 60_000)

  const now = Date.now()
  const isTerminal = status === 'done' || status === 'error'
  const finalSuccess = status === 'done'

  // ── Phase 1: Enviando comando ──
  const p1Status: LifecycleStatus =
    commandIdReceived ? 'success'
    : status === 'error' && !commandIdReceived ? 'failed'
    : status === 'running' ? 'running'
    : 'pending'
  const p1Duration =
    commandIdReceivedAt && startedAt
      ? Math.max(0, commandIdReceivedAt - startedAt)
      : status === 'running' && startedAt && !commandIdReceived
      ? now - startedAt
      : undefined

  // ── Phase 2: Aguardando agente SSM ──
  // Active while SSM status is Pending. Becomes success when InProgress
  // is observed. If we never saw Pending (SSM went straight to
  // InProgress), the phase is still treated as success (instantaneous).
  const p2Status: LifecycleStatus =
    !commandIdReceived ? 'pending'
    : inProgressAt || isTerminal ? 'success'
    : deployStatus === 'Pending' || deployStatus === '' ? 'running'
    : 'success'
  const p2Duration =
    commandIdReceivedAt && inProgressAt
      ? Math.max(0, inProgressAt - commandIdReceivedAt)
      : commandIdReceivedAt && p2Status === 'running'
      ? now - commandIdReceivedAt
      : commandIdReceivedAt && isTerminal && !inProgressAt
      ? 0 // terminal without ever seeing InProgress (instant fail)
      : undefined

  // ── Phase 3: Executando deploy.sh ──
  const p3Status: LifecycleStatus =
    isTerminal ? (finalSuccess ? 'success' : 'failed')
    : (inProgressAt ? 'running' : (commandIdReceived ? 'pending' : 'pending'))
  const p3Duration =
    inProgressAt && endedAt
      ? Math.max(0, endedAt - inProgressAt)
      : inProgressAt && !isTerminal
      ? now - inProgressAt
      : undefined

  const phases: Phase[] = [
    {
      name: 'Enviando comando',
      icon: IconSend,
      status: p1Status,
      durationMs: p1Duration,
    },
    {
      name: 'Aguardando agente SSM',
      icon: IconClock,
      status: p2Status,
      durationMs: p2Duration,
    },
    {
      name: 'Executando deploy.sh',
      icon: IconPlayerPlay,
      status: p3Status,
      durationMs: p3Duration,
    },
  ]

  const activeIdx = phases.findIndex(p => p.status === 'running')

  return (
    <Timeline
      active={activeIdx >= 0 ? activeIdx : phases.findIndex(p => p.status === 'pending')}
      bulletSize={28}
      lineWidth={2}
      role="list"
      aria-label="Etapas do deploy"
      styles={{
        itemTitle: { fontWeight: 600, fontFamily: '"Barlow", sans-serif' },
      }}
    >
      {phases.map(phase => (
        <Timeline.Item
          key={phase.name}
          bullet={<LifecycleBullet phase={phase} />}
          title={
            <Group gap={8} justify="space-between" wrap="nowrap">
              <Text size="sm" fw={600} role="listitem">
                {phase.name}
              </Text>
              {phase.durationMs != null && (
                <Text
                  size="xs"
                  c="dimmed"
                  style={{ fontFamily: '"JetBrains Mono", monospace' }}
                >
                  {formatElapsed(phase.durationMs)}
                </Text>
              )}
            </Group>
          }
        >
          <PhaseHint phase={phase} deployStatus={deployStatus} />
        </Timeline.Item>
      ))}
    </Timeline>
  )
}

function LifecycleBullet({ phase }: { phase: Phase }) {
  if (phase.status === 'success') {
    return (
      <ThemeIcon size={28} radius="xl" color="inovareTeal" variant="filled">
        <IconCircleCheck size={16} />
      </ThemeIcon>
    )
  }
  if (phase.status === 'failed') {
    return (
      <ThemeIcon size={28} radius="xl" color="red" variant="filled">
        <IconCircleX size={16} />
      </ThemeIcon>
    )
  }
  if (phase.status === 'running') {
    return (
      <ThemeIcon
        size={28}
        radius="xl"
        variant="filled"
        color="inovareTeal"
        style={{ boxShadow: `0 0 0 4px ${COLOR.glow}` }}
      >
        <motion.span
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          style={{ display: 'inline-flex' }}
        >
          <IconLoader2 size={16} />
        </motion.span>
      </ThemeIcon>
    )
  }
  return (
    <ThemeIcon size={28} radius="xl" color="gray" variant="light">
      <phase.icon size={16} />
    </ThemeIcon>
  )
}

function PhaseHint({
  phase,
  deployStatus,
}: {
  phase: Phase
  deployStatus: string
}) {
  if (phase.status === 'pending') {
    return (
      <Text size="xs" c="dimmed" pl={4} style={{ fontStyle: 'italic' }}>
        aguardando…
      </Text>
    )
  }
  if (phase.status === 'running') {
    if (phase.name === 'Enviando comando') {
      return (
        <Text size="xs" c="dimmed" pl={4}>
          POST /deploy disparado…
        </Text>
      )
    }
    if (phase.name === 'Aguardando agente SSM') {
      return (
        <Text size="xs" c="dimmed" pl={4}>
          SSM agent processando o comando (status: {deployStatus || 'Pending'})
        </Text>
      )
    }
    if (phase.name === 'Executando deploy.sh') {
      return (
        <Text size="xs" c="dimmed" pl={4}>
          deploy.sh em execução remota — output será coletado ao finalizar
        </Text>
      )
    }
  }
  return null
}
